/**
 * Время набора для отправки на сервер.
 *
 * Сервер принимает `ЧЧ:ММ` и сам решает, сегодня это или завтра: время,
 * которое уже прошло, означает следующий день. Наборы создают на ближайшие
 * часы, поэтому выбирать дату не нужно — достаточно часа и минут.
 *
 * Считается всё в часовом поясе сообщества, а не браузера: модератор может
 * сидеть в другом поясе, и «сегодня» у него начинается в другой момент.
 */

/** Часы и минуты в поясе сообщества прямо сейчас. */
export function currentTime(timeZone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);

  const pick = (type: string): string => parts.find((part) => part.type === type)?.value ?? '00';
  // Полночь в некоторых пределах приходит как «24» — приводим к «00».
  const hour = pick('hour') === '24' ? '00' : pick('hour');

  return `${hour}:${pick('minute')}`;
}

/**
 * День, на который придётся выбранное время: сегодня или завтра.
 *
 * То же правило, что на сервере, — иначе подпись обещала бы одно, а набор
 * создавался бы на другое. Сравниваются строки часов и минут: перевод в
 * абсолютное время потребовал бы смещения пояса, а так сравнение точное.
 *
 * Возвращается только слово: само время подпись показывает отдельно, и
 * повторять его внутри значило бы писать «начало 01:30, завтра в 01:30».
 */
export function dayOfChoice(time: string, timeZone: string, now: Date = new Date()): 'сегодня' | 'завтра' | '' {
  if (!/^\d{2}:\d{2}$/.test(time)) {
    return '';
  }

  return time > currentTime(timeZone, now) ? 'сегодня' : 'завтра';
}

/** Шаг списка времени: четверть часа — привычный интервал в расписаниях. */
export const SLOT_STEP_MINUTES = 15;

export interface TimeSlot {
  /** Время вида `21:15`. */
  time: string;
  /** Наступит ли оно уже завтра. */
  tomorrow: boolean;
}

const pad = (value: number): string => String(value).padStart(2, '0');

/** Минуты от полуночи для `ЧЧ:ММ`. */
export function minutesOfDay(time: string): number {
  const [hour = '0', minute = '0'] = time.split(':');
  return Number.parseInt(hour, 10) * 60 + Number.parseInt(minute, 10);
}

/** `ЧЧ:ММ` из минут от полуночи; сутки закольцованы. */
export function timeFromMinutes(minutes: number): string {
  const wrapped = ((minutes % 1440) + 1440) % 1440;
  return `${pad(Math.floor(wrapped / 60))}:${pad(wrapped % 60)}`;
}

/**
 * Время через столько-то минут от текущего — для кнопок «через полчаса».
 *
 * Считается в поясе сообщества: модератор может сидеть в другом, и «через
 * час» у него должно значить то же самое, что у сообщества.
 */
export function timeAfter(minutes: number, timeZone: string, now: Date = new Date()): string {
  return timeFromMinutes(minutesOfDay(currentTime(timeZone, now)) + minutes);
}

/**
 * Сутки времени вперёд с шагом в четверть часа.
 *
 * Первый слот — ближайший будущий: назначать набор на минуту, которая уже
 * прошла, бессмысленно. Список закольцован через полночь: набор на три часа
 * ночи — обычное дело, а сервер сам поймёт, что это завтра.
 */
export function timeSlots(timeZone: string, now: Date = new Date(), step = SLOT_STEP_MINUTES): TimeSlot[] {
  const nowMinutes = minutesOfDay(currentTime(timeZone, now));
  const first = Math.floor(nowMinutes / step) * step + step;
  const count = Math.floor(1440 / step);

  return Array.from({ length: count }, (_, index) => {
    const absolute = first + index * step;
    return { time: timeFromMinutes(absolute), tomorrow: absolute >= 1440 };
  });
}

/**
 * Разбирает набранное руками время.
 *
 * Принимается всё, чем люди пишут время: `21`, `2100`, `21:00`, `21.00`,
 * `21 00`, `9` — потому что заставлять помнить единственный верный формат
 * и есть та беда, ради которой когда-то завели колесо.
 *
 * `null` — разобрать не вышло.
 */
export function parseTypedTime(text: string): string | null {
  const digits = text.replace(/\D/gu, '');
  if (digits === '' || digits.length > 4) {
    return null;
  }

  // Одна-две цифры — это часы: «9» значит девять ровно.
  const [hour, minute] =
    digits.length <= 2
      ? [Number.parseInt(digits, 10), 0]
      : [Number.parseInt(digits.slice(0, digits.length - 2), 10), Number.parseInt(digits.slice(-2), 10)];

  if (hour > 23 || minute > 59) {
    return null;
  }

  return `${pad(hour)}:${pad(minute)}`;
}

/**
 * Подходит ли слот под набранное.
 *
 * Сравниваются цифры: человек набирает `21`, а в списке `21:00`, `21:15`
 * и `21:30` — все они должны остаться.
 */
export function slotMatches(slot: TimeSlot, query: string): boolean {
  const digits = query.replace(/\D/gu, '');
  return digits === '' || slot.time.replace(':', '').startsWith(digits);
}
